import {
  createAudioVolumeAnalyser,
  createSilenceProbe,
} from '../../../util/audioAnalysis'
import { audioContextController } from '../../../util/AudioContextController'
import type { MicCheckDeps, SilenceCheck } from './MicCheck'
import type { SoundcheckConfig } from './types'

// One set for the page, so a mic proven in one preview (the AV check, the
// device picker) stays proven in the others. A reload checks every mic again.
const provenDevices = new Set<string>()

/**
 * The real browser dependencies for a MicCheck: the digital-zero probe and the
 * worklet speech detector, backed by the shared audio context. Shared by the
 * meeting (through MeetingCubit) and the web hosts (through useMicCheck) so the
 * measurement is identical everywhere.
 */
export function browserMicCheckDeps(
  config: () => SoundcheckConfig,
  silenceCheck: SilenceCheck
): MicCheckDeps {
  return {
    createMeasurement: (track) => createSilenceProbe(track),
    createSpeechDetector: (track, onThresholdCross) =>
      createAudioVolumeAnalyser({ track, onThresholdCross }),
    provenDevices,
    silenceCheck,
    onAudioRunningChange: (listener) =>
      audioContextController.subscribe((state) =>
        listener(state === 'running')
      ),
    now: () => Date.now(),
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (handle) => clearTimeout(handle),
    config: () => {
      const c = config()
      return {
        micGraceMS: c.micGraceMS,
        publishGraceMS: c.publishGraceMS,
        silenceConfirmDelayMS: c.silenceConfirmDelayMS,
        silencePollIntervalMS: c.silencePollIntervalMS,
        nearSilenceRMS: c.nearSilenceRMS,
        // Only previews run near_silence, and a student spends seconds there.
        nearSilenceMS: c.avCheckNearSilenceMS,
      }
    },
  }
}
